import React, { useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { format } from 'date-fns'
import {
  getFormattedDateAndTime,
  SortDirectionEnum,
  TestResult,
  useStateWithDebounce,
  TestType,
  useAllEventAttendees,
  useLazyAllEventAttendees,
  AllEventAttendees,
  PaymentMethod,
  AllEventAttendees_allEventAttendees_attendees,
} from '@modmd/data'
import { useSortChangeHandler } from 'utils/helpers'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import { Box, Container } from 'components/Layout'
import Icon from 'components/Icon'
import TextInput from 'components/TextInput'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { SelectInput } from 'components/SelectInput'
import { DateInput } from 'components/DateInput'
import { COLOR, DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { EventSamplesDatalist } from 'sharedComponents/EventSamplesDatalist'
import { EVENT_PAYMENT_METHOD_OPTIONS, RESULTS_PER_PAGE } from 'internal-portal/constants/filters'
import Button from 'components/Button'
import { useToaster } from 'utils/useToaster'
import papa from 'papaparse'
import { TYPE_OPTIONS } from '../TestSessions/utils'

type TestsProps = RouteComponentProps

type FilterState = {
  result?: TestResult | 'pending'
  memberLike?: string
  location?: string
  dateFrom?: string
  dateTo?: string
  barcode?: string
  type?: TestType
  companyOrGroupLike?: string
  paymentMethod?: PaymentMethod
  qboInvoiceStatus?: string
  insuranceCompanyLike?: string
  page?: string
  direction?: SortDirectionEnum
  sortBy?: string
  resultsPerPage?: string
}

const EventAttendeesList: React.VFC<TestsProps> = () => {
  const isMobile = !useIsMinDevice(DEVICE.TABLET)
  const searchState = useSearchState<FilterState>()
  const { setToastMessage } = useToaster()
  const [isCollapsed, setCollapse] = useState(false)
  const { sorter, handleSortChange } = useSortChangeHandler()
  const { data: attendees, loading: isLoading } = useAllEventAttendees({
    filter: {
      hasResult: searchState.data.result ? searchState.data.result !== 'pending' : undefined,
      result: Object.values(TestResult).some((result) => result === searchState.data.result)
        ? (searchState.data.result as TestResult)
        : undefined,
      emailOrNameLike: searchState.data.memberLike,
      location: searchState.data.location || undefined,
      testedBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
      barcode: searchState.data.barcode,
      type: searchState.data.type || undefined,
      companyOrGroupLike: searchState.data.companyOrGroupLike || undefined,
      paymentMethod: searchState.data.paymentMethod || undefined,
      qboInvoiceStatus: searchState.data.qboInvoiceStatus || undefined,
      insuranceCompanyLike: searchState.data.insuranceCompanyLike || undefined,
    },
    sorter,
    page: Number(searchState.data.page) || 1,
    pageLength: Number(searchState.data.resultsPerPage) || 10,
  })

  const generateCsv = (data: object[]) => {
    const csv = papa.unparse(data, {
      header: true,
    })

    return new Blob([csv], { type: 'text/csv' })
  }

  const createDownloadableLink = (blob: Blob, fileName: string) => {
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.setAttribute('download', `${fileName}.csv`)
    document.body.appendChild(link)

    const click = () => {
      link.click()
      document.body.removeChild(link)
    }

    return { link, click }
  }

  const parseResultIntoCSVModel = (
    attendees: AllEventAttendees_allEventAttendees_attendees[] | null
  ) =>
    attendees?.map((attendee) => {
      const {
        Event,
        EventSamples,
        firstName,
        lastName,
        email,
        date,
        Payment,
        city,
        state,
      } = attendee
      return {
        'Event ID': Event?.id,
        'Event Name': Event?.name,
        'Event Date': format(new Date(date), 'MM/dd/yyyy'),
        'Test Time': EventSamples?.[0]?.testedAt
          ? format(new Date(EventSamples?.[0]?.testedAt), 'MM/dd/yyyy HH:mm')
          : 'Not tested yet',
        'Test Type': Event.testType,
        'Test Status': EventSamples?.[0]?.sampleStatus,
        'Payment Method': Event.paymentMethod,
        'Insurance Name': Payment?.[0]?.insuranceCompany,
        'Insurance ID': Payment?.[0]?.cardID,
        'Patient Name': `${firstName} ${lastName}`,
        City: city,
        State: state,
        'Patient Email': email,
        'Test Result': EventSamples?.[0]?.secondResult
          ? `A: ${EventSamples?.[0]?.result ?? ''} | B: ${EventSamples?.[0]?.secondResult ?? ''}`
          : EventSamples?.[0]?.result,
      }
    })

  const handleDownloadCsv = (result: AllEventAttendees) => {
    if (result?.allEventAttendees?.attendees && result?.allEventAttendees?.attendees.length < 1) {
      setToastMessage('No results found', 'danger')

      return
    }

    const parsedResult = parseResultIntoCSVModel(result?.allEventAttendees?.attendees)
    const dataBlob = generateCsv(parsedResult!)

    createDownloadableLink(dataBlob, `Event-Attendees-${Date.now()}`).click()
  }

  const [getEventAttendees, { loading: lazyEventAttendeeLoading }] = useLazyAllEventAttendees({
    onCompleted: handleDownloadCsv,
  })

  const [filterData, setFilterData] = useStateWithDebounce<FilterState>({}, (filter) => {
    searchState.setSearchState({ ...filter, page: 1 }, false, true)
  })

  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value, name } = event.target
      setFilterData({ ...filterData, [name]: value })
    },
    [filterData, setFilterData]
  )

  const handleGetEventAttendees = React.useCallback(() => {
    getEventAttendees({
      variables: {
        filterData: {
          hasResult: searchState.data.result ? searchState.data.result !== 'pending' : undefined,
          result: Object.values(TestResult).some((result) => result === searchState.data.result)
            ? (searchState.data.result as TestResult)
            : undefined,
          emailOrNameLike: searchState.data.memberLike,
          location: searchState.data.location || undefined,
          testedBetween: [searchState.data.dateFrom || null, searchState.data.dateTo || null],
          barcode: searchState.data.barcode,
          type: searchState.data.type || undefined,
          companyOrGroupLike: searchState.data.companyOrGroupLike || undefined,
          paymentMethod: searchState.data.paymentMethod || undefined,
          qboInvoiceStatus: searchState.data.qboInvoiceStatus || undefined,
          insuranceCompanyLike: searchState.data.insuranceCompanyLike || undefined,
        },
        pagination: {
          from: 0,
          length: attendees?.allEventAttendees?.pagination?.total as number,
        },
      },
    })
  }, [searchState, getEventAttendees, attendees])

  return (
    <DashboardPage>
      <Container>
        <Button
          marginLeft="auto"
          marginBottom="1rem"
          display="flex !important"
          alignItems="center"
          justifyContent="center"
          maxWidth="140px"
          width="100%"
          onClick={handleGetEventAttendees}
          isFetching={lazyEventAttendeeLoading}
        >
          Download CSV
        </Button>
        <EventSamplesDatalist
          data={attendees?.allEventAttendees?.attendees || []}
          pagination={attendees?.allEventAttendees?.pagination}
          isLoading={isLoading}
          tableHeader={
            <Box
              display="grid"
              gridGap="1rem"
              gridTemplateColumns={
                isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(13rem, max-content))'
              }
            >
              <DateInput
                id="dateFrom"
                name="dateFrom"
                label="Date From"
                value={filterData.dateFrom ? new Date(filterData.dateFrom) : null}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateFrom: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <DateInput
                id="dateTo"
                name="dateTo"
                label="Date To"
                value={filterData.dateTo ? new Date(filterData.dateTo) : null}
                onChange={(date) => {
                  setFilterData({
                    ...filterData,
                    dateTo: date ? getFormattedDateAndTime(date) : undefined,
                  })
                }}
                hasBottomContent
                isFullWidth
              />
              <TextInput
                name="memberLike"
                label="Member"
                value={filterData.memberLike || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="ID, name or email"
                hasBottomContent
              />
              <SelectInput
                name="result"
                label="Result"
                value={filterData.result || ''}
                onChange={handleInputChange}
                options={[
                  { value: '', label: 'All' },
                  ...Object.values(TestResult).map((result) => ({
                    value: result,
                    label: result,
                  })),
                  { value: 'pending', label: 'Pending' },
                ]}
                hasBottomContent
              />
              <TextInput
                name="barcode"
                label="Barcode"
                value={filterData.barcode || ''}
                onChange={handleInputChange}
                leftIcon={<Icon.Search />}
                placeholder="Search by Barcode"
                hasBottomContent
              />
              {isCollapsed && (
                <>
                  <TextInput
                    name="companyOrGroupLike"
                    label="Company or Group"
                    value={filterData.companyOrGroupLike || ''}
                    onChange={handleInputChange}
                    leftIcon={<Icon.Search />}
                    placeholder="Company or group name"
                    hasBottomContent
                  />
                  <TextInput
                    name="location"
                    label="Location"
                    value={filterData.location || ''}
                    onChange={handleInputChange}
                    leftIcon={<Icon.Search />}
                    placeholder="Search by Location"
                    hasBottomContent
                  />
                  <TextInput
                    name="insuranceCompanyLike"
                    label="Insurance Company"
                    value={filterData.insuranceCompanyLike || ''}
                    onChange={handleInputChange}
                    leftIcon={<Icon.Search />}
                    placeholder="Company name"
                    hasBottomContent
                  />
                  <SelectInput
                    name="paymentMethod"
                    label="Payment Method"
                    value={filterData.paymentMethod || ''}
                    onChange={handleInputChange}
                    options={EVENT_PAYMENT_METHOD_OPTIONS}
                    hasBottomContent
                  />
                  <SelectInput
                    name="qboInvoiceStatus"
                    label="QB Invoice Status"
                    value={filterData.qboInvoiceStatus || ''}
                    onChange={handleInputChange}
                    options={[
                      { value: '', label: 'All' },
                      { value: 'Invoiced', label: 'Invoiced' },
                      { value: 'Invoicing', label: 'Invoicing' },
                    ]}
                    hasBottomContent
                  />
                  <SelectInput
                    name="type"
                    label="Test Type"
                    value={filterData.type || ''}
                    onChange={handleInputChange}
                    options={TYPE_OPTIONS}
                    hasBottomContent
                  />
                  <SelectInput
                    name="resultsPerPage"
                    label="Results per page"
                    value={filterData.resultsPerPage || ''}
                    onChange={handleInputChange}
                    options={RESULTS_PER_PAGE}
                    hasBottomContent
                  />
                </>
              )}
              <Button onClick={() => setCollapse(!isCollapsed)} appearance="link" size="medium">
                {!isCollapsed ? (
                  <>
                    <Icon.ChevronDown color={COLOR.text} />
                    Show more filters
                  </>
                ) : (
                  <>
                    <Icon.ChevronUp color={COLOR.text} />
                    Hide filters
                  </>
                )}
              </Button>
            </Box>
          }
          columnLabels={{
            location: null,
          }}
          sorter={sorter}
          onSortChange={handleSortChange}
          requestDebounceInMs={250}
          onPageChange={(pageNumber) => {
            searchState.setSearchState({ page: pageNumber }, false)
          }}
          pageLength={attendees?.allEventAttendees?.pagination.length}
        />
      </Container>
    </DashboardPage>
  )
}

export default EventAttendeesList
